<template>
	<div class="cs">
		<div class="cs-head">
			<div class="cs-section-title">Content across all clients</div>
			<div class="cs-head-right">
				<span v-if="stats" class="cs-updated">{{ stats.clients }} clients · updated {{ updatedAgo }}</span>
				<button class="cs-refresh" type="button" :disabled="loading" title="Recalculate statistics" @click="fetch(true)">
					<font-awesome-icon :icon="['fas', 'sync']" :class="{ 'fa-spin': loading }" />
				</button>
			</div>
		</div>

		<div v-if="error && !stats" class="cs-panel cs-empty">Could not load content statistics.</div>
		<div v-else-if="!stats" class="cs-panel cs-empty">
			<font-awesome-icon :icon="['fas', 'spinner']" class="fa-spin" />
		</div>

		<template v-else>
			<!-- KPI tiles -->
			<div class="cs-kpis">
				<div v-for="kpi in kpis" :key="kpi.key" class="cs-kpi">
					<div class="cs-kpi-top">
						<span class="cs-kpi-icon"><font-awesome-icon :icon="['fas', kpi.icon]" /></span>
						<span class="cs-kpi-label">{{ kpi.label }}</span>
					</div>
					<div class="cs-kpi-value">{{ kpi.value }}</div>
					<div class="cs-kpi-note">{{ kpi.note }}</div>
				</div>
			</div>

			<!-- Growth -->
			<div class="cs-grid">
				<div class="cs-panel">
					<div class="cs-panel-title">Pages &amp; News created per month</div>
					<stats-line-chart :data="contentMonthlyData" :options="lineOptions" />
				</div>
				<div class="cs-panel">
					<div class="cs-panel-title">Files uploaded per month</div>
					<stats-bar-chart :data="filesMonthlyData" :options="barOptions({ legend: false })" />
				</div>
			</div>

			<!-- Top clients + file types -->
			<div class="cs-grid cs-grid--wide">
				<div class="cs-panel">
					<div class="cs-panel-head">
						<div class="cs-panel-title">Top 10 clients</div>
						<div class="cs-seg" role="tablist">
							<button
								v-for="m in topMetrics"
								:key="m.key"
								type="button"
								role="tab"
								:aria-selected="topMetric === m.key"
								:class="['cs-seg-btn', { active: topMetric === m.key }]"
								@click="topMetric = m.key"
							>
								{{ m.label }}
							</button>
						</div>
					</div>
					<div v-if="!topRows.length" class="cs-empty cs-empty--inline">No data.</div>
					<stats-bar-chart v-else :key="topMetric" :data="topClientsData" :options="topClientsOptions" />
				</div>
				<div class="cs-panel">
					<div class="cs-panel-title">Files by type</div>
					<ul class="cs-types">
						<li v-for="row in fileTypes" :key="row.label" class="cs-type" :title="`${row.countFull} files · ${row.size}`">
							<span class="cs-type-label">{{ row.label }}</span>
							<span class="cs-type-track"><span class="cs-type-bar" :style="{ width: row.width }"></span></span>
							<span class="cs-type-count">{{ row.count }}</span>
							<span class="cs-type-size">{{ row.size }}</span>
							<span class="cs-type-share">{{ row.share }}</span>
						</li>
					</ul>
				</div>
			</div>

			<!-- Builds -->
			<div class="cs-panel">
				<div class="cs-panel-title">Static builds · last 30 days</div>
				<stats-bar-chart :data="buildsData" :options="buildsOptions" />
			</div>
		</template>
	</div>
</template>

<script>
import juniperApi from '../../../api/juniperApi'
import StatsBarChart from '@/components/charts/StatsBarChart.vue'
import StatsLineChart from '@/components/charts/StatsLineChart.vue'

// Validated categorical palette, assigned in fixed order (never cycled).
const SERIES = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#6250d6', '#e34948']
const CRITICAL = '#e34948'
const INK = '#6b7280'
const GRID = '#eef0f3'

const nf = new Intl.NumberFormat('en-US')
const compact = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 })

function formatBytes(bytes) {
	if (!bytes) return '0 B'
	const units = ['B', 'KB', 'MB', 'GB', 'TB']
	const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
	return `${(bytes / Math.pow(1024, i)).toFixed(i >= 3 ? 1 : 0)} ${units[i]}`
}

function monthLabel(ym) {
	const [y, m] = ym.split('-').map(Number)
	return new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })
}

function statusCount(rows, label) {
	return (rows || []).find(r => r.label === label)?.count || 0
}

export default {
	name: 'ContentStats',
	components: { StatsBarChart, StatsLineChart },
	data() {
		return {
			stats: null,
			loading: false,
			error: false,
			topMetric: 'files',
			topMetrics: [
				{ key: 'files', label: 'Files' },
				{ key: 'storage', label: 'Storage' },
				{ key: 'pages', label: 'Pages' },
				{ key: 'news_notices', label: 'News' },
				{ key: 'views', label: 'Views 30d' },
			],
		}
	},
	computed: {
		updatedAgo() {
			const t = new Date(this.stats?.generated_at).getTime()
			if (!t) return ''
			const min = Math.round((Date.now() - t) / 60000)
			return min < 1 ? 'just now' : `${min} min ago`
		},
		kpis() {
			const t = this.stats.totals
			const origin = this.stats.files.by_origin || []
			const hg = origin.find(o => o.label === 'hg')?.count || 0
			return [
				{ key: 'files', icon: 'folder', label: 'Files', value: compact.format(t.files), note: `${compact.format(t.files - hg)} Town Web · ${compact.format(hg)} HeyGov` },
				{ key: 'storage', icon: 'hdd', label: 'Storage', value: formatBytes(t.storage_bytes), note: `~${formatBytes(t.storage_bytes / Math.max(this.stats.clients, 1))} per client` },
				{ key: 'pages', icon: 'file-alt', label: 'Pages', value: compact.format(t.pages), note: `${nf.format(statusCount(this.stats.pages_by_status, 'draft'))} drafts` },
				{ key: 'news', icon: 'newspaper', label: 'News & Notices', value: compact.format(t.news_notices), note: `${nf.format(statusCount(this.stats.news_by_status, 'draft'))} drafts · ${nf.format(statusCount(this.stats.news_by_status, 'future'))} scheduled` },
				{ key: 'views', icon: 'eye', label: 'Views · 30 days', value: compact.format(t.views_last_30_days), note: `${compact.format(t.views_all_time)} all time` },
				{ key: 'subscribers', icon: 'envelope', label: 'Subscribers', value: compact.format(t.subscribers), note: `${nf.format(t.alerts)} alerts` },
				{ key: 'departments', icon: 'sitemap', label: 'Departments', value: nf.format(t.departments), note: `${nf.format(t.menus)} menus` },
				{ key: 'faqs', icon: 'question-circle', label: 'FAQs', value: nf.format(t.faqs), note: `${nf.format(t.posts)} posts · ${nf.format(t.redirections)} redirects` },
			]
		},
		monthLabels() {
			return this.stats.monthly.months.map(monthLabel)
		},
		contentMonthlyData() {
			const s = this.stats.monthly.series
			return {
				labels: this.monthLabels,
				datasets: [
					this.lineDataset('Pages', s.pages, SERIES[0]),
					this.lineDataset('News & Notices', s.news_notices, SERIES[1]),
				],
			}
		},
		filesMonthlyData() {
			return {
				labels: this.monthLabels,
				datasets: [this.barDataset('Files', this.stats.monthly.series.files, SERIES[0])],
			}
		},
		lineOptions() {
			return {
				...this.baseOptions({ legend: true }),
				interaction: { mode: 'index', intersect: false },
				elements: { point: { radius: 0, hoverRadius: 5, hitRadius: 12 } },
			}
		},
		topRows() {
			return this.stats.top_clients[this.topMetric] || []
		},
		topClientsData() {
			return {
				labels: this.topRows.map(r => r.name),
				datasets: [this.barDataset(this.topMetrics.find(m => m.key === this.topMetric).label, this.topRows.map(r => r.value), SERIES[0])],
			}
		},
		topClientsOptions() {
			const isBytes = this.topMetric === 'storage'
			const fmt = v => (isBytes ? formatBytes(v) : nf.format(v))
			const opts = this.barOptions({ legend: false, horizontal: true, format: fmt })
			opts.aspectRatio = 2.1
			opts.onClick = (evt, elements) => {
				const row = elements?.length ? this.topRows[elements[0].index] : null
				if (row) this.$router.push('/juniper/clients/edit/' + row.id)
			}
			opts.onHover = (evt, elements) => {
				if (evt?.native?.target) evt.native.target.style.cursor = elements?.length ? 'pointer' : 'default'
			}
			opts.scales.x.ticks.callback = v => (isBytes ? formatBytes(v) : compact.format(v))
			return opts
		},
		fileTypes() {
			const rows = this.stats.files.by_type
			const max = Math.max(...rows.map(r => r.count), 1)
			const total = rows.reduce((sum, r) => sum + r.count, 0) || 1
			return rows.map(r => ({
				label: r.label,
				count: compact.format(r.count),
				countFull: nf.format(r.count),
				size: formatBytes(r.bytes),
				share: `${((r.count / total) * 100).toFixed(r.count / total < 0.01 ? 2 : 1)}%`,
				// keep tiny buckets visible as a sliver
				width: `${Math.max((r.count / max) * 100, 0.8)}%`,
			}))
		},
		buildsData() {
			const b = this.stats.builds
			return {
				labels: b.days.map(d => new Date(d + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
				datasets: [
					this.barDataset('Partial', b.partial, SERIES[0]),
					this.barDataset('Full', b.full, SERIES[2]),
					this.barDataset('Failed', b.failed, CRITICAL),
				],
			}
		},
		buildsOptions() {
			const opts = this.barOptions({ legend: true, stacked: true })
			opts.aspectRatio = 5
			opts.interaction = { mode: 'index', intersect: false }
			return opts
		},
	},
	created() {
		this.fetch()
	},
	methods: {
		fetch(refresh = false) {
			this.loading = true
			this.error = false
			juniperApi
				.get('/statistics/content-overview', { params: refresh ? { refresh: 1 } : {} })
				.then(({ data }) => {
					this.stats = data
				})
				.catch(() => {
					this.error = true
				})
				.finally(() => {
					this.loading = false
				})
		},
		lineDataset(label, data, color) {
			return { label, data, borderColor: color, backgroundColor: color, borderWidth: 2, tension: 0.3, fill: false }
		},
		barDataset(label, data, color) {
			return {
				label,
				data,
				backgroundColor: color,
				hoverBackgroundColor: color,
				borderColor: '#fff',
				borderWidth: { top: 0, bottom: 0, left: 0, right: 0 },
				borderRadius: 4,
				borderSkipped: 'start',
				maxBarThickness: 28,
			}
		},
		baseOptions({ legend = false, format = v => nf.format(v) } = {}) {
			return {
				responsive: true,
				maintainAspectRatio: true,
				aspectRatio: 2.6,
				animation: { duration: 300 },
				plugins: {
					legend: {
						display: legend,
						position: 'top',
						align: 'end',
						labels: { boxWidth: 10, boxHeight: 10, usePointStyle: true, pointStyle: 'rectRounded', color: INK, font: { size: 12 } },
					},
					tooltip: {
						backgroundColor: '#111827',
						padding: 10,
						cornerRadius: 8,
						boxPadding: 4,
						callbacks: {
							label: ctx => `${ctx.dataset.label}: ${format(ctx.parsed[ctx.chart.options.indexAxis === 'y' ? 'x' : 'y'])}`,
						},
					},
				},
				scales: {
					x: { grid: { display: false, drawBorder: false }, ticks: { color: INK, font: { size: 11 }, maxRotation: 0, autoSkipPadding: 8 } },
					y: { beginAtZero: true, grid: { color: GRID, drawBorder: false }, ticks: { color: INK, font: { size: 11 }, precision: 0, callback: v => compact.format(v) } },
				},
			}
		},
		barOptions({ legend = false, horizontal = false, stacked = false, format } = {}) {
			const opts = this.baseOptions({ legend, format })
			if (stacked) {
				opts.scales.x.stacked = true
				opts.scales.y.stacked = true
			}
			if (horizontal) {
				opts.indexAxis = 'y'
				opts.scales = {
					x: { beginAtZero: true, grid: { color: GRID, drawBorder: false }, ticks: { color: INK, font: { size: 11 } } },
					y: { grid: { display: false, drawBorder: false }, ticks: { color: '#374151', font: { size: 12 } } },
				}
			}
			return opts
		},
	},
}
</script>

<style scoped>
.cs {
	display: flex;
	flex-direction: column;
	gap: 16px;
}

.cs-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
}

.cs-section-title,
.cs-panel-title {
	font-size: 13px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	color: #6b7280;
}

.cs-head-right {
	display: flex;
	align-items: center;
	gap: 10px;
}

.cs-updated {
	font-size: 12px;
	color: #9ca3af;
}

.cs-refresh {
	width: 30px;
	height: 30px;
	border-radius: 8px;
	border: 1px solid #e5e7eb;
	background: #fff;
	color: #6b7280;
	font-size: 12px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
}

.cs-refresh:hover:not(:disabled) {
	color: #1565c0;
	border-color: #bfdbfe;
}

.cs-refresh:disabled {
	cursor: default;
	opacity: 0.7;
}

.cs-panel {
	background: #fff;
	border-radius: 12px;
	padding: 16px 18px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
	min-width: 0;
}

.cs-panel > .cs-panel-title {
	margin-bottom: 10px;
}

.cs-panel-head {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	flex-wrap: wrap;
	margin-bottom: 10px;
}

.cs-empty {
	min-height: 160px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #6b7280;
	font-size: 13px;
}

.cs-empty--inline {
	min-height: 120px;
}

/* KPI tiles */
.cs-kpis {
	display: grid;
	grid-template-columns: repeat(4, minmax(0, 1fr));
	gap: 12px;
}

.cs-kpi {
	background: #fff;
	border-radius: 10px;
	padding: 12px 14px;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
	display: flex;
	flex-direction: column;
	gap: 4px;
	min-width: 0;
}

.cs-kpi-top {
	display: flex;
	align-items: center;
	gap: 7px;
}

.cs-kpi-icon {
	width: 22px;
	height: 22px;
	border-radius: 6px;
	background: #f3f4f6;
	color: #4b5563;
	font-size: 11px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.cs-kpi-label {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: #6b7280;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.cs-kpi-value {
	font-size: 22px;
	font-weight: 700;
	color: #111827;
	line-height: 1.15;
}

.cs-kpi-note {
	font-size: 11px;
	color: #6b7280;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

@media (min-width: 1800px) {
	.cs-kpis {
		grid-template-columns: repeat(8, minmax(0, 1fr));
	}
}

@media (max-width: 700px) {
	.cs-kpis {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

/* Files by type list */
.cs-types {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.cs-type {
	display: grid;
	grid-template-columns: 84px minmax(0, 1fr) 48px 64px 48px;
	align-items: center;
	gap: 10px;
	font-size: 12px;
	color: #374151;
}

.cs-type-label {
	font-weight: 500;
}

.cs-type-track {
	height: 10px;
	background: #f3f4f6;
	border-radius: 4px;
	overflow: hidden;
}

.cs-type-bar {
	display: block;
	height: 100%;
	background: #1baf7a;
	border-radius: 4px;
}

.cs-type-count {
	font-weight: 600;
	color: #111827;
	text-align: right;
}

.cs-type-size,
.cs-type-share {
	color: #6b7280;
	text-align: right;
	white-space: nowrap;
}

/* Chart grids */
.cs-grid {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
}

.cs-grid--wide {
	grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
}

@media (max-width: 1100px) {
	.cs-grid,
	.cs-grid--wide {
		grid-template-columns: minmax(0, 1fr);
	}
}

/* Segmented control */
.cs-seg {
	display: inline-flex;
	background: #f3f4f6;
	border-radius: 8px;
	padding: 2px;
	gap: 2px;
}

.cs-seg-btn {
	border: 0;
	background: transparent;
	font-size: 12px;
	font-weight: 500;
	color: #6b7280;
	padding: 4px 10px;
	border-radius: 6px;
	cursor: pointer;
}

.cs-seg-btn.active {
	background: #fff;
	color: #111827;
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
</style>
